import type { CSSProperties } from "react";
import type { GroupInfo } from "../lib/api";
import { getUserId, isAdmin } from "../lib/identity";
import { useGame } from "../store";
import { useGameConnection } from "./useGameConnection";
import { CueCard } from "./CueCard";
import { Captions } from "./Captions";
import { Controls } from "./Controls";
import { PdfOverlay } from "./PdfOverlay";
import { ClueModal } from "./ClueModal";
import { AdminBar } from "./AdminBar";

interface Props {
  group: GroupInfo;
}

/**
 * The live experience, laid out to match the original LRCFS scaffold: a large
 * main puzzle screen, a leather-desk "terminal" holding a paper note with the
 * secondary puzzle and clue/skip controls, captions overlaid at the bottom,
 * full-image cue cards, and a hypotheses iframe as the right-hand panel.
 */
export function Stage({ group }: Props): React.ReactElement {
  const userId = getUserId();
  const themeId = group.codename;
  useGameConnection({ groupId: group.group_id, userId, themeId });

  const puzzlePrimary = useGame((s) => s.puzzlePrimary);
  const puzzleSecondary = useGame((s) => s.puzzleSecondary);
  const puzzleVisible = useGame((s) => s.puzzleVisible);

  const asset = (p: string) => `url(/api/get_file/${themeId}/files/${p})`;
  const scaffoldVars = {
    "--leather": asset("images/brown-leather.jpg"),
    "--paper": asset("images/paper-texture2.png"),
    "--fade": asset("cue-cards/fade.png"),
  } as CSSProperties;

  const hypUrl = `/api/load_puzzle/${themeId}/files/puzzle/hypotheses.html?uid=${group.group_id}`;

  return (
    <div id="scaffold" style={scaffoldVars}>
      {isAdmin() && <AdminBar groupId={group.group_id} />}

      <div className="main_screen">
        <CueCard />

        <div className="top_element">
          <div className="content_col">
            <div id="content_area">
              {puzzlePrimary && puzzleVisible && (
                <iframe id="external_puzzle_screen" title="puzzle" src={puzzlePrimary} />
              )}
              <Captions />
            </div>

            <div id="terminal_container">
              <div id="terminal_area">
                <div id="paper">
                  <Controls groupId={group.group_id} />
                  {puzzleSecondary && (
                    <iframe
                      id="secondary_external_puzzle_screen"
                      title="secondary-puzzle"
                      src={puzzleSecondary}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="hyp_col">
            <iframe id="hyp_iframe" title="hypotheses" src={hypUrl} />
          </div>
        </div>
      </div>

      <PdfOverlay />
      <ClueModal />
    </div>
  );
}
